import { useMemo, useCallback } from 'react';
import { Order, SavedLocation } from '@hero/schema';
import { MapLocationData } from '@hero/web-components';
import { convertToLatLng } from '@hero/core';

export interface UseOrderMapOptions {
  order: Order;
  mode?: 'view' | 'edit' | 'auto';
}

export interface UseOrderMapReturn {
  /** Converted map locations from order destinations */
  mapLocations: MapLocationData[];
  /** Whether edit mode is enabled */
  isEditMode: boolean;
  /** Marker config function */
  getMarkerConfig: (
    location: MapLocationData,
    index: number,
  ) => {
    card: {
      title: string;
      info: string;
    };
  };
}

/**
 * Hook to manage order map data conversion
 * Converts order destinations to map format and provides marker config
 * Note: Editing state is managed by GoogleMapWithMarker component internally
 */
export const useOrderMap = ({ order, mode = 'view' }: UseOrderMapOptions): UseOrderMapReturn => {
  // Convert order destinations to MapLocationData format
  const mapLocations: MapLocationData[] = useMemo(() => {
    if (!order.destinations || order.destinations.length === 0) {
      return [];
    }

    // Sort destinations: pickup first, then dropoff
    const sortedDestinations = [...order.destinations].sort((a, b) => {
      if (a.type === 'pickup' && b.type === 'dropoff') return -1;
      if (a.type === 'dropoff' && b.type === 'pickup') return 1;
      return 0;
    });

    return sortedDestinations.map((destination, index) => {
      // Convert location from backend format (GeoJSON) to LatLng format
      let cords: { lat: number; lng: number };

      if (destination.address?.location) {
        cords = convertToLatLng(destination.address.location);
      } else {
        cords = { lat: 0, lng: 0 };
      }

      // Build minimal savedLocation from order destination so LocationCard shows name/contacts and does not show Save button
      const savedLocation = destination.savedPlace
        ? ({
            _id: destination.savedPlace._id,
            name: destination.savedPlace.name,
            contacts: destination.contacts?.length
              ? destination.contacts.map((c, i) => ({
                  ...c,
                  isPrimary: (i === 0) as boolean,
                }))
              : [],
            customer: destination.beneficiary,
          } as unknown as SavedLocation)
        : undefined;

      const data: MapLocationData = {
        cords,
        savedLocationId: destination.savedPlace?._id,
        savedLocation,
        isAccurateLocation: destination.isAccurateLocation,
        locationUrl: destination.locationUrl,
        data: {
          type: destination.type,
        },
        city: destination.address?.city?.name,
        fullAddress: destination.address?.fullAddress,
        address: destination.address,
      };
      return data;
    });
  }, [order.destinations]);

  // Determine if edit mode is enabled
  const isEditMode = mode === 'edit' || mode === 'auto';

  // Get marker config - customize icons based on type
  const getMarkerConfig = useCallback((location: MapLocationData, index: number) => {
    const isPickup = location.data?.type === 'pickup' || index === 0;
    const title =
      location.savedLocation?.name ||
      location.fullAddress ||
      location.city ||
      `Location ${index + 1}`;

    // Build info string with contact and customer info
    const infoParts: string[] = [];
    if (location.fullAddress) {
      infoParts.push(location.fullAddress);
    }
    // relations-v2: the saved location's owning party is `org` (the
    // legacy `customer` field was dropped from the embedded snapshot).
    if (location.savedLocation?.org?.name) {
      infoParts.push(`Customer: ${location.savedLocation.org.name}`);
    }
    if (location.savedLocation?.contacts && location.savedLocation.contacts.length > 0) {
      const primaryContact =
        location.savedLocation.contacts.find(c => c.isPrimary) ||
        location.savedLocation.contacts[0];
      if (primaryContact) {
        infoParts.push(
          `Contact: ${primaryContact.name}${
            primaryContact.phone ? ` (${primaryContact.phone})` : ''
          }`,
        );
      }
    }

    return {
      card: {
        title,
        info: infoParts.join(' • '),
      },
    };
  }, []);

  return {
    mapLocations,
    isEditMode,
    getMarkerConfig,
  };
};
